import { FivetranServiceEnum, klaviyoSchema } from "@repo/fivetran/types";
import { Icon } from "@repo/ui/components/icon";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui/popover";
import { Button } from "@repo/ui/ui/button";
import { Calendar } from "@repo/ui/ui/calendar";
import { Field, FieldLabel } from "@repo/ui/ui/field";
import { format } from "date-fns";

import {
  DestinationField,
  DestinationSetupField,
  SchemaField,
} from "./connection-fields";
import { ConnectionFormShell } from "./connection-form-shell";
import {
  type ConnectionFormProps,
  useConnectionForm,
} from "./use-connection-form";

/**
 * Human-readable labels for Klaviyo's `historical_sync_limit` config values,
 * keyed by the raw Fivetran value so the trigger renders the label instead of
 * the raw enum.
 */
const SYNC_LIMIT_LABELS: Record<string, string> = {
  ALL_TIME: "All time",
  DATE_LIMIT: "Start date",
};

const SYNC_LIMIT_OPTIONS = Object.entries(SYNC_LIMIT_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/** Setup form for the Klaviyo Fivetran connector (API-key authentication). */
export const KlaviyoFormPage = ({
  onConnected,
  onCancel,
}: ConnectionFormProps = {}) => {
  const { form, embedded, formId } = useConnectionForm({
    service: FivetranServiceEnum.enum.klaviyo,
    schema: klaviyoSchema,
    defaultConfig: {
      schema: FivetranServiceEnum.enum.klaviyo,
      api_key: "",
      historical_sync_limit: "ALL_TIME",
      historical_sync_limit_date: "",
    },
    onConnected,
  });

  return (
    <ConnectionFormShell
      form={form}
      formId={formId}
      icon="klaviyo"
      submitLabel="Create"
      embedded={embedded}
      onCancel={onCancel}
    >
      <DestinationField form={form} />
      <DestinationSetupField form={form} />
      <SchemaField form={form} />

      <form.AppField name="config.api_key">
        {(field) => (
          <field.PasswordField
            label="API Key"
            placeholder="pk_XXXXXXXXXXXXXXXXXXXXXXXX"
          />
        )}
      </form.AppField>

      <div className="grid grid-cols-2 gap-4">
        <form.AppField name="config.historical_sync_limit">
          {(field) => (
            <field.SelectField
              label="Sync time frame"
              placeholder="Select time frame"
              options={SYNC_LIMIT_OPTIONS}
              formatValue={(value) => SYNC_LIMIT_LABELS[value] ?? value}
            />
          )}
        </form.AppField>
        <form.Subscribe
          selector={(state) => state.values.config.historical_sync_limit}
        >
          {(historicalSyncLimit) => (
            <form.AppField name="config.historical_sync_limit_date">
              {(field) => {
                const value = field.state.value as string;
                const isDisabled = historicalSyncLimit !== "DATE_LIMIT";
                return (
                  <Field className="flex-1">
                    <FieldLabel htmlFor={field.name}>
                      Sync start date
                    </FieldLabel>
                    <Popover>
                      <PopoverTrigger
                        render={
                          <Button
                            variant="outline"
                            className="w-full justify-between font-normal"
                            disabled={isDisabled}
                          />
                        }
                      >
                        {value
                          ? format(new Date(value), "MM/dd/yyyy")
                          : "Select date"}
                        <Icon name="chevron-down" className="size-4" />
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto overflow-hidden p-0"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          captionLayout="dropdown"
                          selected={value ? new Date(value) : undefined}
                          onSelect={(date) =>
                            field.handleChange(
                              format(date ?? new Date(), "yyyy-MM-dd"),
                            )
                          }
                          disabled={isDisabled}
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>
                );
              }}
            </form.AppField>
          )}
        </form.Subscribe>
      </div>
    </ConnectionFormShell>
  );
};
